import { BrowserRouter as Router, Navigate, Route, Routes, useParams } from "react-router";
import AuthCallback from "@/pages/AuthPages/AuthCallback";
import NotFound from "@/pages/OtherPage/NotFound";
import AppLayout from "@/layout/AppLayout";
import { ScrollToTop } from "@/components/common/ScrollToTop";
import VersionTag from "@/components/VersionTag";
import Home from "@/pages/Dashboard/Home";
import ChangePasswordPage from "@/pages/AuthPages/ChangePasswordPage";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import CoursesPage from "@/pages/Academy/CoursesPage";
import CourseManagerPage from "@/pages/Academy/CourseManagerPage";
import LessonFormPage from "@/pages/Academy/LessonFormPage";
import InstructorHub from "@/pages/Instructor/InstructorHub";
import InstructorStatsPage from "@/pages/Instructor/InstructorStatsPage";
import InstitutionsPage from "@/pages/Programs/InstitutionsPage";
import InstitutionFormPage from "@/pages/Programs/InstitutionFormPage";
import SeedbedsPage from "@/pages/Programs/SeedbedsPage";
import SeedbedHealthStatsPage from "@/pages/Programs/SeedbedHealthStatsPage";
import SeedbedFormPage from "@/pages/Programs/SeedbedFormPage";
import SeedbedDetailPage from "@/pages/Programs/SeedbedDetailPage";
import ProgramsPage from "@/pages/Programs/ProgramsPage";
import ProgramDetailPage from "@/pages/Programs/ProgramDetailPage";
import ProgramFormPage from "@/pages/Programs/ProgramFormPage";
import SubmissionsManager from "@/pages/Instructor/SubmissionsManager";
import SectionQuizzesOverviewPage from "@/pages/Instructor/SectionQuizzesOverviewPage";
import SectionQuizAttemptsPage from "@/pages/Instructor/SectionQuizAttemptsPage";
import SectionQuizRegradePage from "@/pages/Instructor/SectionQuizRegradePage";
import SectionQuizTestPage from "@/pages/Instructor/SectionQuizTestPage";
import SectionQuizTestResultPage from "@/pages/Instructor/SectionQuizTestResultPage";
import SectionQuizTestsPage from "@/pages/Instructor/SectionQuizTestsPage";
import SectionQuizAttemptDetailPage from "@/pages/Instructor/SectionQuizAttemptDetailPage";
import StudentsManager from "@/pages/Instructor/StudentsManager";
import StudentEnrollPage from "@/pages/Instructor/StudentEnrollPage";
import StudentProfilePage from "@/pages/Instructor/StudentProfilePage";
import StudentCreatePage from "@/pages/Instructor/StudentCreatePage";
import StudentsImportPage from "@/pages/Instructor/StudentsImportPage";
import GroupsManager from "@/pages/Instructor/GroupsManager";
import MyGroupPage from "@/pages/Instructor/MyGroupPage";
import GroupManagerPage from "@/pages/Instructor/GroupManagerPage";
import VideosManager from "@/pages/Instructor/VideosManager";
import VideoFormPage from "@/pages/Instructor/VideoFormPage";
import CertificatesManager from "@/pages/Instructor/CertificatesManager";
import CertificateTemplateListPage from "@/pages/Certificates/CertificateTemplateListPage";
import CertificateTemplateEditorPage from "@/pages/Certificates/CertificateTemplateEditorPage";
import CCCDDashboardPage from "@/pages/CCCD/CCCDDashboardPage";
import CCCDFunnelStatsPage from "@/pages/CCCD/CCCDFunnelStatsPage";
import TeamDetailPage from "@/pages/CCCD/TeamDetailPage";
import TeamCreatePage from "@/pages/CCCD/TeamCreatePage";
import TeamsImportPage from "@/pages/CCCD/TeamsImportPage";
import ManualPaymentPage from "@/pages/CCCD/ManualPaymentPage";
import EventsListPage from "@/pages/CCCD/EventsListPage";
import EventFormPage from "@/pages/CCCD/EventFormPage";
import EventDetailPage from "@/pages/CCCD/EventDetailPage";
import CategoryFormPage from "@/pages/CCCD/CategoryFormPage";
import SponsorsPage from "@/pages/CCCD/SponsorsPage";
import SponsorFormPage from "@/pages/CCCD/SponsorFormPage";
import CheckInEventsPage from "@/pages/CCCD/CheckInEventsPage";
import CheckInScanPage from "@/pages/CCCD/CheckInScanPage";
import CheckInReportPage from "@/pages/CCCD/CheckInReportPage";
import PlanFormPage from "@/pages/CCCD/PlanFormPage";
import RegionalsListPage from "@/pages/CCCD/RegionalsListPage";
import RegionalFormPage from "@/pages/CCCD/RegionalFormPage";
import CatalogOptionsPage from "@/pages/CCCD/CatalogOptionsPage";
import AddonsCatalogPage from "@/pages/CCCD/AddonsCatalogPage";
import CategoryExpertsPage from "@/pages/CCCD/CategoryExpertsPage";
import EventMeetWindowsPage from "@/pages/CCCD/EventMeetWindowsPage";
import AccountProfilePage from "@/pages/Settings/AccountProfilePage";
import AccountSecurityPage from "@/pages/Settings/AccountSecurityPage";
import AccountIntegrationsPage from "@/pages/Settings/AccountIntegrationsPage";
import ScoringParametersPage from "@/pages/CCCD/ScoringParametersPage";
import ScoringParameterFormPage from "@/pages/CCCD/ScoringParameterFormPage";
import CatalogItemsPage from "@/pages/CCCD/CatalogItemsPage";
import SupplyRequestsPage from "@/pages/CCCD/SupplyRequestsPage";
import AcademicInstitutionsPage from "@/pages/CCCD/AcademicInstitutionsPage";
import AcademicInstitutionFormPage from "@/pages/CCCD/AcademicInstitutionFormPage";
import AcademicInstitutionsImportPage from "@/pages/CCCD/AcademicInstitutionsImportPage";
import RegionalDetailPage from "@/pages/CCCD/RegionalDetailPage";
import WaitlistListPage from "@/pages/TRA/WaitlistListPage";
import WaitlistStatsPage from "@/pages/TRA/WaitlistStatsPage";
import WaitlistDetailPage from "@/pages/TRA/WaitlistDetailPage";
import AddWaitlistEntryPage from "@/pages/TRA/AddWaitlistEntryPage";
import EditionsListPage from "@/pages/TRA/EditionsListPage";
import EditionDetailPage from "@/pages/TRA/EditionDetailPage";
import MembersListPage from "@/pages/TRA/MembersListPage";
import MemberDetailPage from "@/pages/TRA/MemberDetailPage";
import StaffUsersPage from "@/pages/Settings/StaffUsersPage";
import StaffUserFormPage from "@/pages/Settings/StaffUserFormPage";
import RolesListPage from "@/pages/Settings/RolesListPage";
import AdminVisibilityDashboardPage from "@/pages/Settings/AdminVisibilityDashboardPage";
import SupportAccountsPage from "@/pages/Settings/SupportAccountsPage";
import RoleFormPage from "@/pages/Settings/RoleFormPage";

// Editar un semillero ahora es la pestaña "Información" de SeedbedDetailPage
// (misma vista que crear/ver), así que el viejo link /edit solo redirige ahí.
function SeedbedEditRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/programs/seedbeds/${id}`} replace />;
}

export default function App() {
  return (
    <>
      <VersionTag />
      <Router>
        <ScrollToTop />
        <Routes>
          {/* Dashboard Layout */}
          <Route element={<AppLayout />}>
            <Route
              index
              path="/"
              element={
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <ProtectedRoute>
                  <ChangePasswordPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/academy/courses"
              element={
                <ProtectedRoute requires="academy:courses:view">
                  <CoursesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/academy/courses/new"
              element={
                <ProtectedRoute requires="academy:courses:edit">
                  <CourseManagerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/academy/courses/:courseId/lessons/new"
              element={
                <ProtectedRoute requires="academy:courses:edit">
                  <LessonFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/academy/courses/:courseId/lessons/:lessonId/edit"
              element={
                <ProtectedRoute requires="academy:courses:edit">
                  <LessonFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/academy/courses/:id"
              element={
                <ProtectedRoute requires="academy:courses:view">
                  <CourseManagerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/hub"
              element={
                <ProtectedRoute requires="academy:instructor-hub:view">
                  <InstructorHub />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/stats"
              element={
                <ProtectedRoute requires="academy:instructor-hub:view">
                  <InstructorStatsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/submissions"
              element={
                <ProtectedRoute requires="academy:submissions:view">
                  <SubmissionsManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/section-quizzes"
              element={
                <ProtectedRoute requires="academy:quizzes-teach:view">
                  <SectionQuizzesOverviewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/section-quizzes/:sectionQuizId/attempts"
              element={
                <ProtectedRoute requires="academy:quizzes-teach:view">
                  <SectionQuizAttemptsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/section-quizzes/:sectionQuizId/test"
              element={
                <ProtectedRoute requires="academy:quizzes-teach:edit">
                  <SectionQuizTestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/section-quizzes/:sectionQuizId/tests"
              element={
                <ProtectedRoute requires="academy:quizzes-teach:edit">
                  <SectionQuizTestsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/section-quizzes/tests/:runId"
              element={
                <ProtectedRoute requires="academy:quizzes-teach:edit">
                  <SectionQuizTestResultPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/section-quizzes/:sectionQuizId/regrade"
              element={
                <ProtectedRoute requires="academy:quizzes-teach:edit">
                  <SectionQuizRegradePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/section-quizzes/attempts/:attemptId"
              element={
                <ProtectedRoute requires="academy:quizzes-teach:view">
                  <SectionQuizAttemptDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/students"
              element={
                <ProtectedRoute requires="academy:students:view">
                  <StudentsManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/students/new"
              element={
                <ProtectedRoute requires="academy:students:edit">
                  <StudentCreatePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/students/import"
              element={
                <ProtectedRoute requires="academy:students:edit">
                  <StudentsImportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/students/enroll"
              element={
                <ProtectedRoute requires="academy:students:edit">
                  <StudentEnrollPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/students/:id/perfil"
              element={
                <ProtectedRoute requires="academy:students:view">
                  <StudentProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/groups"
              element={
                <ProtectedRoute requires="academy:groups:view">
                  <GroupsManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/my-group"
              element={
                <ProtectedRoute requires="academy:my-group:view">
                  <MyGroupPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/groups/new"
              element={
                <ProtectedRoute requires="academy:groups:view">
                  <GroupManagerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/groups/:id"
              element={
                <ProtectedRoute requires="academy:groups:view">
                  <GroupManagerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/videos"
              element={
                <ProtectedRoute requires="academy:videos:view">
                  <VideosManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/videos/upload"
              element={
                <ProtectedRoute requires="academy:videos:edit">
                  <VideoFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/videos/:id/edit"
              element={
                <ProtectedRoute requires="academy:videos:edit">
                  <VideoFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/certificates"
              element={
                <ProtectedRoute requires="academy:certificates:view">
                  <CertificatesManager />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/certificate-templates"
              element={
                <ProtectedRoute requires="academy:certificates:view">
                  <CertificateTemplateListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/instructor/certificate-templates/:id"
              element={
                <ProtectedRoute requires="academy:certificates:edit">
                  <CertificateTemplateEditorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/programs/programs"
              element={<ProtectedRoute requires="academy:programs:view"><ProgramsPage /></ProtectedRoute>}
            />
            <Route
              path="/programs/programs/new"
              element={<ProtectedRoute requires="academy:programs:edit"><ProgramFormPage /></ProtectedRoute>}
            />
            <Route
              path="/programs/programs/:id/edit"
              element={<ProtectedRoute requires="academy:programs:edit"><ProgramFormPage /></ProtectedRoute>}
            />
            <Route
              path="/programs/programs/:id"
              element={<ProtectedRoute requires="academy:programs:view"><ProgramDetailPage /></ProtectedRoute>}
            />
            <Route
              path="/programs/institutions"
              element={<ProtectedRoute requires="academy:institutions:view"><InstitutionsPage /></ProtectedRoute>}
            />
            <Route
              path="/programs/institutions/new"
              element={<ProtectedRoute requires="academy:institutions:edit"><InstitutionFormPage /></ProtectedRoute>}
            />
            <Route
              path="/programs/institutions/:id/edit"
              element={<ProtectedRoute requires="academy:institutions:edit"><InstitutionFormPage /></ProtectedRoute>}
            />
            <Route
              path="/programs/seedbeds"
              element={<ProtectedRoute requires="academy:seedbeds:view"><SeedbedsPage /></ProtectedRoute>}
            />
            <Route
              path="/programs/seedbeds/stats"
              element={<ProtectedRoute requires="academy:seedbeds:view"><SeedbedHealthStatsPage /></ProtectedRoute>}
            />
            <Route
              path="/programs/seedbeds/new"
              element={<ProtectedRoute requires="academy:seedbeds:edit"><SeedbedFormPage /></ProtectedRoute>}
            />
            <Route
              path="/programs/seedbeds/:id/edit"
              element={<ProtectedRoute requires="academy:seedbeds:edit"><SeedbedEditRedirect /></ProtectedRoute>}
            />
            <Route
              path="/programs/seedbeds/:id"
              element={<ProtectedRoute requires="academy:seedbeds:view"><SeedbedDetailPage /></ProtectedRoute>}
            />
            <Route
              path="/academy/seedbeds"
              element={<Navigate to="/programs/seedbeds" replace />}
            />
            <Route path="/cccd" element={<ProtectedRoute requires="cccd:panel:view"><CCCDDashboardPage /></ProtectedRoute>} />
            <Route path="/cccd/funnel-stats" element={<ProtectedRoute requires="cccd:panel:view"><CCCDFunnelStatsPage /></ProtectedRoute>} />
            <Route path="/cccd/teams/new" element={<ProtectedRoute requires="cccd:panel:edit"><TeamCreatePage /></ProtectedRoute>} />
            <Route path="/cccd/teams/import" element={<ProtectedRoute requires="cccd:panel:edit"><TeamsImportPage /></ProtectedRoute>} />
            <Route path="/cccd/teams/:id" element={<ProtectedRoute requires="cccd:panel:view"><TeamDetailPage /></ProtectedRoute>} />
            <Route path="/cccd/teams/:teamId/payments/new" element={<ProtectedRoute requires="cccd:addons:edit"><ManualPaymentPage /></ProtectedRoute>} />
            <Route path="/cccd/events" element={<ProtectedRoute requires="cccd:events-national:view"><EventsListPage /></ProtectedRoute>} />
            <Route path="/cccd/events/new" element={<ProtectedRoute requires="cccd:events-national:create"><EventFormPage /></ProtectedRoute>} />
            <Route path="/cccd/events/:id" element={<ProtectedRoute requires="cccd:events-national:view"><EventDetailPage /></ProtectedRoute>} />
            <Route path="/cccd/events/:eventId/categories/new" element={<ProtectedRoute requires="cccd:addons:edit"><CategoryFormPage /></ProtectedRoute>} />
            <Route path="/cccd/events/:eventId/categories/:categoryId/edit" element={<ProtectedRoute requires="cccd:addons:edit"><CategoryFormPage /></ProtectedRoute>} />
            <Route path="/cccd/events/:eventId/plans/new" element={<ProtectedRoute requires="cccd:addons:edit"><PlanFormPage /></ProtectedRoute>} />
            <Route path="/cccd/events/:eventId/plans/:planId/edit" element={<ProtectedRoute requires="cccd:addons:edit"><PlanFormPage /></ProtectedRoute>} />
            <Route path="/cccd/regionals" element={<ProtectedRoute requires="cccd:events-regional:view"><RegionalsListPage /></ProtectedRoute>} />
            <Route path="/cccd/regionals/new" element={<ProtectedRoute requires="cccd:events-regional:edit"><RegionalFormPage /></ProtectedRoute>} />
            <Route path="/cccd/regionals/:id" element={<ProtectedRoute requires="cccd:events-regional:view"><RegionalDetailPage /></ProtectedRoute>} />
            {/* Patrocinadores por evento (puntos-sponsors-and-live-cache): nacional y regional. */}
            <Route path="/cccd/events/:eventId/sponsors" element={<ProtectedRoute requires="cccd:panel:view"><SponsorsPage /></ProtectedRoute>} />
            <Route path="/cccd/events/:eventId/sponsors/new" element={<ProtectedRoute requires="cccd:panel:edit"><SponsorFormPage /></ProtectedRoute>} />
            <Route path="/cccd/events/:eventId/sponsors/:sponsorId/edit" element={<ProtectedRoute requires="cccd:panel:edit"><SponsorFormPage /></ProtectedRoute>} />
            <Route path="/cccd/regionals/:regionalId/sponsors" element={<ProtectedRoute requires="cccd:panel:view"><SponsorsPage /></ProtectedRoute>} />
            <Route path="/cccd/regionals/:regionalId/sponsors/new" element={<ProtectedRoute requires="cccd:panel:edit"><SponsorFormPage /></ProtectedRoute>} />
            <Route path="/cccd/regionals/:regionalId/sponsors/:sponsorId/edit" element={<ProtectedRoute requires="cccd:panel:edit"><SponsorFormPage /></ProtectedRoute>} />
            <Route path="/cccd/regionals/:regionalId/categories/new" element={<ProtectedRoute requires="cccd:addons:edit"><CategoryFormPage /></ProtectedRoute>} />
            <Route path="/cccd/regionals/:regionalId/categories/:categoryId/edit" element={<ProtectedRoute requires="cccd:addons:edit"><CategoryFormPage /></ProtectedRoute>} />
            <Route path="/cccd/regionals/:regionalId/plans/new" element={<ProtectedRoute requires="cccd:addons:edit"><PlanFormPage /></ProtectedRoute>} />
            <Route path="/cccd/regionals/:regionalId/plans/:planId/edit" element={<ProtectedRoute requires="cccd:addons:edit"><PlanFormPage /></ProtectedRoute>} />
            <Route path="/cccd/checkin" element={<ProtectedRoute requires="cccd:checkin:view"><CheckInEventsPage /></ProtectedRoute>} />
            <Route path="/cccd/checkin/event/:eventId" element={<ProtectedRoute requires="cccd:checkin:view"><CheckInScanPage /></ProtectedRoute>} />
            <Route path="/cccd/checkin/regional/:regionalEventId" element={<ProtectedRoute requires="cccd:checkin:view"><CheckInScanPage /></ProtectedRoute>} />
            <Route path="/cccd/checkin/report" element={<ProtectedRoute requires="cccd:checkin:view"><CheckInReportPage /></ProtectedRoute>} />
            <Route path="/cccd/checkin/report/event/:eventId" element={<ProtectedRoute requires="cccd:checkin:view"><CheckInReportPage /></ProtectedRoute>} />
            <Route path="/cccd/checkin/report/regional/:regionalEventId" element={<ProtectedRoute requires="cccd:checkin:view"><CheckInReportPage /></ProtectedRoute>} />
            <Route path="/cccd/catalog-options" element={<ProtectedRoute requires="cccd:catalog-options:view"><CatalogOptionsPage /></ProtectedRoute>} />
            <Route path="/cccd/addons" element={<ProtectedRoute requires="cccd:addons:view"><AddonsCatalogPage /></ProtectedRoute>} />
            <Route path="/cccd/category-experts" element={<ProtectedRoute requires="cccd:panel:view"><CategoryExpertsPage /></ProtectedRoute>} />
            <Route path="/cccd/event-meet-windows" element={<ProtectedRoute requires="cccd:panel:view"><EventMeetWindowsPage /></ProtectedRoute>} />
            <Route path="/settings/calendly" element={<Navigate to="/account/integrations" replace />} />
            <Route path="/account" element={<ProtectedRoute><AccountProfilePage /></ProtectedRoute>} />
            <Route path="/account/security" element={<ProtectedRoute><AccountSecurityPage /></ProtectedRoute>} />
            <Route path="/account/integrations" element={<ProtectedRoute><AccountIntegrationsPage /></ProtectedRoute>} />
            <Route path="/cccd/scoring-parameters" element={<ProtectedRoute requires="cccd:scoring:view"><ScoringParametersPage /></ProtectedRoute>} />
            <Route path="/cccd/scoring-parameters/new" element={<ProtectedRoute requires="cccd:scoring:create"><ScoringParameterFormPage /></ProtectedRoute>} />
            <Route path="/cccd/scoring-parameters/:parameterId/edit" element={<ProtectedRoute requires="cccd:scoring:edit"><ScoringParameterFormPage /></ProtectedRoute>} />
            <Route path="/cccd/supply-catalog" element={<ProtectedRoute requires="cccd:supply-catalog:view"><CatalogItemsPage /></ProtectedRoute>} />
            <Route path="/cccd/supply-requests" element={<ProtectedRoute requires="cccd:supply-catalog:view"><SupplyRequestsPage /></ProtectedRoute>} />
            <Route path="/cccd/academic-institutions" element={<ProtectedRoute requires="cccd:academic-institutions:view"><AcademicInstitutionsPage /></ProtectedRoute>} />
            <Route path="/cccd/academic-institutions/new" element={<ProtectedRoute requires="cccd:academic-institutions:edit"><AcademicInstitutionFormPage /></ProtectedRoute>} />
            <Route path="/cccd/academic-institutions/import" element={<ProtectedRoute requires="cccd:academic-institutions:edit"><AcademicInstitutionsImportPage /></ProtectedRoute>} />
            <Route path="/cccd/academic-institutions/:id/edit" element={<ProtectedRoute requires="cccd:academic-institutions:edit"><AcademicInstitutionFormPage /></ProtectedRoute>} />
            <Route path="/tra/waitlist" element={<ProtectedRoute requires="tripoli:waitlist:view"><WaitlistListPage /></ProtectedRoute>} />
            <Route path="/tra/stats" element={<ProtectedRoute requires="tripoli:waitlist:view"><WaitlistStatsPage /></ProtectedRoute>} />
            <Route path="/tra/waitlist/new" element={<ProtectedRoute requires="tripoli:waitlist:edit"><AddWaitlistEntryPage /></ProtectedRoute>} />
            <Route path="/tra/waitlist/:id" element={<ProtectedRoute requires="tripoli:waitlist:view"><WaitlistDetailPage /></ProtectedRoute>} />
            <Route path="/tra/editions" element={<ProtectedRoute requires="tripoli:editions:view"><EditionsListPage /></ProtectedRoute>} />
            <Route path="/tra/editions/new" element={<ProtectedRoute requires="tripoli:editions:edit"><EditionDetailPage /></ProtectedRoute>} />
            <Route path="/tra/editions/:id" element={<ProtectedRoute requires="tripoli:editions:view"><EditionDetailPage /></ProtectedRoute>} />
            <Route path="/tra/members" element={<ProtectedRoute requires="tripoli:members:view"><MembersListPage /></ProtectedRoute>} />
            <Route path="/tra/members/:id" element={<ProtectedRoute requires="tripoli:members:view"><MemberDetailPage /></ProtectedRoute>} />
            <Route
              path="/settings/visibility"
              element={
                <ProtectedRoute requires="admin:visibility-dashboard:view">
                  <AdminVisibilityDashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/support-accounts"
              element={
                <ProtectedRoute requires="support:accounts:view">
                  <SupportAccountsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/users"
              element={
                <ProtectedRoute requires="staff:manage-accounts:view">
                  <StaffUsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/users/new"
              element={
                <ProtectedRoute requires="staff:manage-accounts:edit">
                  <StaffUserFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/roles"
              element={
                <ProtectedRoute requires="staff:manage-roles:view">
                  <RolesListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/roles/new"
              element={
                <ProtectedRoute requires="staff:manage-roles:edit">
                  <RoleFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/roles/:id/edit"
              element={
                <ProtectedRoute requires="staff:manage-roles:edit">
                  <RoleFormPage />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Auth Layout */}
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Fallback Route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </>
  );
}
